// Constants
import { IProductCard } from '@src/components/product-card/product-card.types'
import { API_SF_RUNTIME_BASE_URL } from '@src/config/env'
import { SUBSCRIPTION_EXP_ENABLED_HEADER } from '@src/constants/api.headers.constants'
import ProductOptionValue, { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import ProductTag from '@src/quince-site-common/src/domains/ProductTag'
import {
  GetProductRequest,
  GetProductRequestWithContentfulProductIds,
  GetProductRequestWithInternalProductIds,
  GetProductRequestWithSpids,
  GetSearchProductRequest,
  ProductServiceOptions,
} from '@src/quince-site-common/src/services/productService/productService.types'
import { getCommaSeparatedAdditionalAttributesValueString } from '@src/quince-site-common/src/utils/additionalAtrributeUtility'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { createBatches } from '@src/utils/general.utils'

import { PRODUCT_FILTER_KEYS, PRODUCT_SORT_VALUES } from '../../constants/product'
import { FEATURES, MODULES } from '../../constants/template-info'
import Product, { IProduct } from '../../domains/Product/Product'
import { DeliveryDetailsByColorType } from '../../domains/ProductDeliveryDetails'
import { IProductPurchaseInfo, SvDetails } from '../../domains/ProductPurchaseInfo'
import { PromoConfig } from '../../domains/ProductVariant/PromoConfig'
import QuinceApiError from '../../domains/quince-api-error'
import productHelper, { CUSTOM_FIELD_NAMES } from '../../helpers/productHelper'
import { getContentProductsEnv } from '../../utils/getEnvironmentParam'
import { bffClient, sandServicesHttpClient, storefrontRuntimeHttpClient } from '../../utils/HttpClient'
import { getSvDetailsByIdForProductVariants } from '../checkoutService/CheckoutProductService'

import {
  fetchAndEnrichProductsByContentfulIdsUsingBatching,
  fetchAndEnrichProductsByInternalProductIdsUsingBatching,
  fetchAndEnrichProductsBySpidsUsingBatching,
} from './fetchAndEnrichProducts'

/** Batch size for {@link ProductService.getProductCardsBatched} (wishlist-only). */
const WISHLIST_PRODUCT_CARDS_BATCH_SIZE = 150

interface GetOrderedProductsRequest {
  productIds: number[]
  filters?: Record<PRODUCT_FILTER_KEYS, string>
  sortValue?: PRODUCT_SORT_VALUES
  additionalAttributes?: Record<string, string>
  limit?: number
  useLite?: boolean
}

interface IProductFeedResponse {
  data: {
    product: Product | null
    color_order: string[]
    website_product: Product
  }[]
  next_url: string | null
  total_counts: number
}

class ProductService {
  private getProductCardId(product: Product, selectedColor: string) {
    return `${product.id}_${selectedColor}`
  }

  public async getOrderedProducts(request: GetOrderedProductsRequest) {
    try {
      const initialUrlParams = new URLSearchParams({
        ...request.filters,
        productIds: request.productIds.join(','),
        sortBy: String(request.sortValue),
        limit: String(request.limit),
        useLite: String(!!request.useLite),
        environment: getContentProductsEnv(),
      })
      const additionalAttribute = getCommaSeparatedAdditionalAttributesValueString(request.additionalAttributes)
      if (additionalAttribute) {
        initialUrlParams.set('additionalAttribute', additionalAttribute)
      }

      let nextUrl: URL | null = new URL(`${API_SF_RUNTIME_BASE_URL}/get-all-products?${initialUrlParams.toString()}`)
      const allUniqueProductCardIDs: string[] = []

      while (nextUrl) {
        const url: URL = new URL(nextUrl)
        const paginatedProductFeedData = await storefrontRuntimeHttpClient.get<IProductFeedResponse>(url.href)
        allUniqueProductCardIDs.push(
          ...paginatedProductFeedData.data.data
            .filter(productData => Boolean(productData.website_product))
            .map(({ website_product, color_order }) => this.getProductCardId(website_product, color_order[0] ?? '')),
        )
        nextUrl = paginatedProductFeedData.data.next_url ? new URL(paginatedProductFeedData.data.next_url) : null
      }
      return allUniqueProductCardIDs
    } catch (e) {
      const quinceError = new QuinceApiError({
        error: e as Error,
        // @i18n-ignore
        message: 'Failed while fetching products in product service getOrderedProducts() runtime',
        apiName: 'ALL_PRODUCT_FETCH',
      })
      captureException(quinceError, {
        module: MODULES.PRODUCT_SERVICE,
        feature: FEATURES.FILTER_CHANGE,
        extraInfo: {
          capturedFrom: 'getOrderedProducts',
        },
      })
      return []
    }
  }

  public async getProductDynamicDataByProductIds(
    productIds: (number | string)[],
    useLite = false,
    zipCode?: string | undefined,
    subscriptionExpEnabled?: boolean,
  ) {
    try {
      const { data: tagsAndDeliveryInfoWithProductId } = await sandServicesHttpClient.get<
        Pick<
          IProduct,
          | 'id'
          | 'tags'
          | 'delivery_info'
          | 'new_color_options'
          | 'productOptions'
          | 'isWhiteGloveDeliveryAvailable'
          | 'variants'
          | 'subscriptionEnabled'
        >[]
      >('/products/enrich', {
        params: {
          internalProductIds: productIds.join(','),
          options: [
            ProductServiceOptions.ENRICH_ONLY_TAGS,
            ...(subscriptionExpEnabled ? [ProductServiceOptions.ENRICH_SUBSCRIPTION] : []),
          ].join(','),
          useLite,
          zipCode,
          environment: getContentProductsEnv(),
        },
        headers: {
          [SUBSCRIPTION_EXP_ENABLED_HEADER]: subscriptionExpEnabled ? 'true' : 'false',
        },
      })

      const productIdVsDeliveryInfo: Record<number, DeliveryDetailsByColorType | undefined> = {}
      const productIdVsTags: Record<number, ProductTag[]> = {}
      const colorOrderVsProductId: Record<number, ProductOptionValue[] | undefined> = {}
      const newColorOrderVsProductId: Record<number, ProductOptionValue[] | undefined> = {}
      const isWhiteGloveProductVsProductId: Record<number, boolean> = {}
      const promoConfigVsProductId: Record<number, PromoConfig[] | undefined | null> = {}
      const subscriptionEnabledVsProductId: Record<number, boolean> = {}

      tagsAndDeliveryInfoWithProductId.forEach(productIdTagDeliveryInfoTuple => {
        productIdVsTags[productIdTagDeliveryInfoTuple.id] = productIdTagDeliveryInfoTuple.tags
        productIdVsDeliveryInfo[productIdTagDeliveryInfoTuple.id] = productIdTagDeliveryInfoTuple.delivery_info
        newColorOrderVsProductId[productIdTagDeliveryInfoTuple.id] = productIdTagDeliveryInfoTuple.new_color_options
        colorOrderVsProductId[productIdTagDeliveryInfoTuple.id] = productIdTagDeliveryInfoTuple.productOptions?.find?.(
          option => option.name === ProductOptionName.Color,
        )?.values
        isWhiteGloveProductVsProductId[productIdTagDeliveryInfoTuple.id] =
          !!productIdTagDeliveryInfoTuple.isWhiteGloveDeliveryAvailable
        productIdTagDeliveryInfoTuple.variants?.forEach(variant => {
          promoConfigVsProductId[variant.id] = variant.promoConfig
        })
        subscriptionEnabledVsProductId[productIdTagDeliveryInfoTuple.id] =
          productIdTagDeliveryInfoTuple.subscriptionEnabled
      })

      return {
        productIdVsTags,
        productIdVsDeliveryInfo,
        colorOrderVsProductId,
        newColorOrderVsProductId,
        isWhiteGloveProductVsProductId,
        promoConfigVsProductId,
        subscriptionEnabledVsProductId,
      }
    } catch (e) {
      const quinceError = new QuinceApiError({
        error: e as Error,
        // @i18n-ignore
        message: 'Failed while fetching product tags getProductDynamicDataByProductIds() runtime',
        apiName: 'WEBSITE_PRODUCT_FETCH',
      })
      captureException(quinceError, {
        module: MODULES.PRODUCT_SERVICE,
        feature: FEATURES.GET_PRODUCT,
        extraInfo: {
          capturedFrom: 'getProductDynamicDataByProductIds',
        },
      })
      return {}
    }
  }

  public async getProductCards(
    {
      productIds,
      spids,
      colors,
    }: {
      productIds?: string[]
      spids?: string[]
      colors?: string[]
    },
    subscriptionExpEnabled?: boolean,
  ) {
    if (!spids?.length && !productIds?.length) {
      return []
    }
    const requestParams = {
      spids: spids?.join(','),
      productIds: productIds?.join(','),
      // @i18n-ignore
      type: 'DEFAULT',
      colors: colors?.join(','),
    }
    try {
      const {
        data: { data },
      } = await bffClient.get<{ data: IProductCard[] }>('/v1/product/get-product-cards', {
        params: requestParams,
        headers: {
          [SUBSCRIPTION_EXP_ENABLED_HEADER]: subscriptionExpEnabled ? 'true' : 'false',
        },
      })
      return data
    } catch (e) {
      throw new QuinceApiError({
        error: e as Error,
        // @i18n-ignore
        message: 'Failed while fetching product cards getProductCards() runtime',
        apiName: 'BFF_PRODUCT_CARDS_FETCH',
        data: {
          requestParams,
        },
      })
    }
  }

  /**
   * Fetches product cards in parallel batches using Promise.allSettled.
   * Individual batch failures are silently dropped from the result — only
   * fulfilled batches contribute to the returned array. Each rejected batch
   * is reported to RUM via captureException so failures are observable
   * without breaking the overall request.
   */
  public async getProductCardsBatched(
    {
      spids,
      colors,
    }: {
      spids: string[]
      colors: string[]
    },
    subscriptionExpEnabled = false,
  ): Promise<IProductCard[]> {
    if (!spids.length) return []

    const batches = createBatches(spids, WISHLIST_PRODUCT_CARDS_BATCH_SIZE)
    const colorBatches = createBatches(colors, WISHLIST_PRODUCT_CARDS_BATCH_SIZE)

    const settled = await Promise.allSettled(
      batches.map((batch, index) =>
        this.getProductCards(
          {
            spids: batch,
            colors: colorBatches[index],
          },
          subscriptionExpEnabled,
        ),
      ),
    )

    return settled.flatMap((result, index) => {
      if (result.status === 'fulfilled') return result.value

      captureException(result.reason, {
        module: MODULES.WISHLIST,
        feature: FEATURES.FETCH_WISHLIST_PRODUCTS,
        extraInfo: { batchIndex: index, batchSpids: batches[index] },
      })
      return []
    })
  }

  public async getProducts(
    fetchProductRequest?: GetProductRequest,
    subscriptionExpEnabled = false,
  ): Promise<Product[]> {
    if (fetchProductRequest?.spids?.length) {
      return await fetchAndEnrichProductsBySpidsUsingBatching(
        fetchProductRequest as GetProductRequestWithSpids,
        subscriptionExpEnabled,
      )
    } else if (fetchProductRequest?.contentfulProductIds?.length) {
      return fetchAndEnrichProductsByContentfulIdsUsingBatching(
        fetchProductRequest as GetProductRequestWithContentfulProductIds,
        subscriptionExpEnabled,
      )
    } else if (fetchProductRequest?.internalProductIds?.length) {
      return fetchAndEnrichProductsByInternalProductIdsUsingBatching(
        fetchProductRequest as GetProductRequestWithInternalProductIds,
        subscriptionExpEnabled,
      )
    }
    return []
  }

  public async getSearchResults(fetchProductRequest?: GetSearchProductRequest): Promise<{ data: IProductCard[] }> {
    try {
      const { data } = await bffClient.get<{ data: IProductCard[] }>('v1/search/search-results', {
        params: fetchProductRequest,
      })
      return data
    } catch (e) {
      throw new QuinceApiError({
        error: e as Error,
        // @i18n-ignore
        message: 'Failed while fetching website product getSearchResults() runtime',
        apiName: 'WEBSITE_SEARCH_PRODUCT_FETCH',
        data: {
          fetchProductRequest,
        },
      })
    }
  }

  public async getProductPurchaseInfo(
    product: Product,
    isB2BSelfService?: boolean,
  ): Promise<IProductPurchaseInfo> {
    const svidVsSvDetails = await getSvDetailsByIdForProductVariants(product, isB2BSelfService)
    const svidShowMTOMap = this.getShowMadeToOrderInfoForProductVariants(product, svidVsSvDetails)

    return {
      svidShowMTOMap,
      svidVsSvDetails,
    }
  }

  public async getProductVariantIdToInventoryCount(productId: number) {
    const { data: variantToInventoryMap } = await storefrontRuntimeHttpClient.get<Record<string, number>>(
      '/get-variant-inventory-info',
      {
        params: {
          productId,
        },
      },
    )

    return variantToInventoryMap
  }

  /**
   * This returns whether to Show MTO for a variant or not.
   * For scenarios, where MTO is present in SF location, then don't show MTO.
   * Uses the isInventoryPresentAtSFWarehouse field from the active-inventory API response.
   */
  private getShowMadeToOrderInfoForProductVariants(
    product: Product,
    svidVsSvDetails: Record<number, SvDetails>,
  ): Record<number, boolean> {
    if (!productHelper.getIsTruthyCustomField(product, CUSTOM_FIELD_NAMES.IS_MTO)) {
      return {}
    }
    const variantIdShowMTOMap: Record<number, boolean> = {}
    for (const variant of product.variants ?? []) {
      const svDetails = svidVsSvDetails[variant.id]
      // Show MTO if inventory is NOT present at SF warehouse
      if (svDetails && !svDetails.isInventoryPresentAtSFWarehouse) {
        variantIdShowMTOMap[variant.id] = true
      }
    }
    return variantIdShowMTOMap
  }

  getProductsBySpids = async (spids: (number | string)[]) => {
    const products = await this.getProducts({
      spids: spids,
      useLite: true,
      options: [ProductServiceOptions.ENRICH_REFRESH_LITE],
    })
    const productBySpid: Record<string, Product> = {}

    // This is fix for 100+ variants
    products.forEach(product => {
      const spids = Array.from(new Set((product.variants ?? []).map(variant => variant.spid)))
      spids.forEach(spid => {
        productBySpid[spid] = product
      })
    })

    return productBySpid
  }
}

const productService = new ProductService()

export default productService
